import { useEffect, useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Table } from 'core-components/library/list'
import type { IColumn } from 'core-components/library/list/types'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tag as BaseTag } from 'partner360/components/tag'
import type { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { scoreCards } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { ScoreCard } from 'partner360/features/keyInitiatives/ui/common/scoreCard/ScoreCard'

import type { getInitiativesPlanQuality } from '../../helpers/getInitiativesPlanQuality'
import { QualityPillar } from '../qualityPillar/QualityPillar'

import styles from './StrategicAlignmentPillar.module.scss'

const KEYS = TRANSLATION_KEYS.quality.strategicAlignment

interface IProps {
  quality: ReturnType<typeof getInitiativesPlanQuality>['strategicGrowthAreas']
  onEditInitiative?: (initiative: TKeyInitiative) => void
  canEdit?: boolean
  /** Told which of the card's own panels stand open, whenever that changes — for a consumer that
   *  repeats the card elsewhere, such as the cockpit's export. */
  onViewChange?: (view: { isBreakdownVisible: boolean; isInitiativesVisible: boolean }) => void
}

export const StrategicAlignmentPillar = ({
  quality,
  onEditInitiative,
  canEdit = false,
  onViewChange,
}: IProps) => {
  const t = useSafetyTranslation()
  const [isScoreInfoVisible, setIsScoreInfoVisible] = useState(false)
  const [isBreakdownVisible, setIsBreakdownVisible] = useState(false)
  const [isInitiativesVisible, setIsInitiativesVisible] = useState(false)

  useEffect(() => {
    onViewChange?.({ isBreakdownVisible, isInitiativesVisible })
  }, [onViewChange, isBreakdownVisible, isInitiativesVisible])

  const mdfCount = quality.mdfInitiatives.length
  const priorityCount = quality.initiativesWithBusinessPrioritiesCount
  const rawScore = (mdfCount + priorityCount) * 50
  const overlapCount = quality.mdfInitiatives.filter(({ BusinessPriorities }) =>
    BusinessPriorities?.split(',').some((uid) => uid.trim())
  ).length
  const gaps = quality.initiativesWithoutBusinessPriorities
  const columns = useMemo<IColumn<TKeyInitiative>[]>(
    () => [
      {
        field: 'Name',
        title: TRANSLATION_KEYS.quality.details.name,
        titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.name} />,
        renderFunction: (item) => item.Name || '',
      },
      {
        field: 'Development',
        title: TRANSLATION_KEYS.dashboard.table.columns.strategicInitiative,
        titleRender: () => (
          <Translation text={TRANSLATION_KEYS.dashboard.table.columns.strategicInitiative} />
        ),
        renderFunction: (item) => <Translation text={item.Development || ''} />,
      },
      {
        field: 'Description',
        title: TRANSLATION_KEYS.quality.details.description,
        titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.description} />,
        renderFunction: (item) => item.Description || '',
      },
    ],
    []
  )
  const rowActions = useMemo(
    () => [
      {
        id: 'edit',
        action: (item: TKeyInitiative) => onEditInitiative?.(item),
        title: t(TRANSLATION_KEYS.quality.details.edit),
        icon: 'kbase kbase-edit kbase-sm',
      },
    ],
    [onEditInitiative, t]
  )

  return (
    <>
      <QualityPillar
        title={KEYS.title}
        score={quality.score}
        band={quality.band}
        progressBand={quality.band}
        description={KEYS.groupSummary}
        descriptionReplaceParams={[
          { key: '{mdfCount}', value: String(mdfCount) },
          { key: '{priorityCount}', value: String(priorityCount) },
        ]}
        descriptionAction={
          <button
            type="button"
            className={styles.breakdownToggle}
            onClick={() => setIsBreakdownVisible((value) => !value)}
          >
            <Translation text={isBreakdownVisible ? KEYS.hideBreakdown : KEYS.showBreakdown} />
          </button>
        }
        beforeActions={
          isBreakdownVisible && (
            <div className={styles.breakdown}>
              {[
                { title: KEYS.mdfGroup, count: mdfCount },
                { title: KEYS.priorityGroup, count: priorityCount },
              ].map(({ title, count }) => (
                <div key={title} className={styles.calculationRow}>
                  <span>
                    <Translation text={title} />
                  </span>
                  <span className={styles.formula}>{count} × 50%</span>
                  <span className={styles.contribution}>{count * 50}%</span>
                </div>
              ))}
              <div className={styles.totalRow}>
                <span>
                  <Translation text={KEYS.scoreLabel} />
                </span>
                <div className={styles.formula}>
                  {rawScore > 100 && (
                    <BaseTag
                      variant="neutral"
                      translation={
                        <Translation
                          text={KEYS.cappedScore}
                          replaceParams={[{ key: '{score}', value: String(rawScore) }]}
                        />
                      }
                    />
                  )}
                </div>
                <span className={styles.contribution}>{quality.score}%</span>
              </div>
              {overlapCount > 0 && (
                <div className={styles.overlap}>
                  <Translation
                    text={overlapCount === 1 ? KEYS.overlapOne : KEYS.overlapMany}
                    replaceParams={[{ key: '{count}', value: String(overlapCount) }]}
                  />
                </div>
              )}
            </div>
          )
        }
        leadingAction={
          gaps.length > 0 ? (
            <Button
              variant={isInitiativesVisible ? 'contained' : 'outlined'}
              onClick={() => setIsInitiativesVisible((value) => !value)}
            >
              <Translation
                text={isInitiativesVisible ? KEYS.hideWithoutPriority : KEYS.showWithoutPriority}
              />
            </Button>
          ) : quality.mdfInitiatives.length + priorityCount > 0 ? (
            <span>
              <Translation text={KEYS.allHavePriority} />
            </span>
          ) : null
        }
        onHowScore={() => setIsScoreInfoVisible(true)}
      >
        {isInitiativesVisible && (
          <div className={styles.initiatives}>
            <Table
              items={gaps}
              columns={columns}
              keyField="EntryId"
              header
              rowActions={canEdit && onEditInitiative ? rowActions : undefined}
              rowActionsVisible={canEdit && onEditInitiative ? 'visible' : 'hidden'}
            />
          </div>
        )}
      </QualityPillar>
      <ModalDialog
        show={isScoreInfoVisible}
        onClose={() => setIsScoreInfoVisible(false)}
        contentProps={{ scoreCard: scoreCards[2] }}
        content={ScoreCard}
        size="xs"
      />
    </>
  )
}
